import React, { Suspense, lazy, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { htmlToPlainText } from './sanitizeStudentHtml'

/** What the student produced: rich-editor HTML, or raw textarea text (fallback). */
export type StudentAnswerFormat = 'html' | 'plain'

/** One change from the answer box. The API normalises both formats to stored HTML. */
export type StudentAnswerChange = { value: string; format: StudentAnswerFormat }

/** Visible characters a text answer may hold; the API enforces the same cap after normalisation. */
export const ANSWER_MAX_VISIBLE_CHARS = 20000

/**
 * Imperative handle for whoever must not race the debounce: `flush` emits the
 * pending change now (through onChange) and returns it; `cancel` drops it
 * without emitting and disables the unmount flush (an answer-type switch).
 */
export type RichTextAnswerController = {
	flush(): StudentAnswerChange | undefined
	cancel(): void
}

export type RichTextAnswerProps = {
	/** Stored value: always HTML (the API normalises every text answer on write). */
	value?: string | null
	/** Called with the current content ('' when empty), debounced by `delay`. */
	onChange: (change: StudentAnswerChange) => void
	/** Debounce in ms; 0 emits on every change. Pending change is FLUSHED on
	 * unmount unless `cancel()` was called (the old textarea cancelled it and
	 * lost the last second). */
	delay?: number
	disabled?: boolean
	placeholder?: string
	/** Visible-character cap; typing past it is refused and a counter is shown. */
	maxLength?: number
	/** Populated while mounted, null otherwise. Not a forwardRef: React 17's
	 * lazy does not forward refs, and the controller belongs to this outer
	 * component anyway (it exists before the chunk loads and after a fallback). */
	controllerRef?: React.MutableRefObject<RichTextAnswerController | null>
}

export const loadEditor = () => import('./RichTextAnswerEditor')
const Editor = lazy(loadEditor)

/**
 * If the editor chunk cannot load (offline mid-exam, a deploy that rotated
 * chunk hashes), the exam must not crash: fall back to a plain textarea. It
 * declares its text as `plain`; the server escapes it.
 */
class EditorBoundary extends React.Component<{ fallback: React.ReactNode }, { failed: boolean }> {
	state = { failed: false }
	static getDerivedStateFromError() {
		return { failed: true }
	}
	componentDidCatch() {
		// no-op: the fallback is the recovery
	}
	render() {
		return this.state.failed ? this.props.fallback : this.props.children
	}
}

type InnerProps = Omit<RichTextAnswerProps, 'onChange' | 'controllerRef'> & { onChange: (change: StudentAnswerChange) => void }

const PlainFallback: React.FC<InnerProps> = ({ value, onChange, disabled, placeholder, maxLength }) => (
	<FallbackArea
		defaultValue={htmlToPlainText(value ?? '')}
		disabled={disabled}
		placeholder={placeholder}
		maxLength={maxLength}
		onChange={e => onChange({ value: e.target.value, format: 'plain' })}
	/>
)

/**
 * The student's rich text answer box. Quill only lands in the exam /
 * assignment chunks (React.lazy), never in the main bundle.
 */
const RichTextAnswer: React.FC<RichTextAnswerProps> = props => {
	const { onChange, delay = 0, controllerRef, maxLength = ANSWER_MAX_VISIBLE_CHARS } = props
	const timer = useRef<number>()
	const pending = useRef<StudentAnswerChange | null>(null)
	const cancelled = useRef(false)
	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange

	const flush = useCallback((): StudentAnswerChange | undefined => {
		window.clearTimeout(timer.current)
		timer.current = undefined
		if (pending.current === null) return undefined
		const change = pending.current
		pending.current = null
		onChangeRef.current(change)
		return change
	}, [])

	const cancel = useCallback(() => {
		window.clearTimeout(timer.current)
		timer.current = undefined
		pending.current = null
		cancelled.current = true
	}, [])

	const handleChange = (change: StudentAnswerChange) => {
		// New input re-arms the unmount flush after a cancel.
		cancelled.current = false
		if (!delay) {
			onChangeRef.current(change)
			return
		}
		pending.current = change
		window.clearTimeout(timer.current)
		timer.current = window.setTimeout(flush, delay)
	}

	useEffect(() => {
		if (!controllerRef) return
		controllerRef.current = { flush, cancel }
		return () => {
			controllerRef.current = null
		}
	}, [controllerRef, flush, cancel])

	// Flush on unmount: a "Next" click within the debounce window must still
	// deliver the last keystrokes. Skipped after cancel(): an answer-type switch
	// already erased the text, and re-emitting it would resurrect it under the
	// old type (this runs in the passive phase, AFTER the switch was committed).
	useEffect(
		() => () => {
			if (!cancelled.current) flush()
		},
		[flush]
	)

	const inner = { ...props, maxLength }
	return (
		<EditorBoundary fallback={<PlainFallback {...inner} onChange={handleChange} />}>
			<Suspense fallback={<Placeholder />}>
				<Editor {...inner} onChange={html => handleChange({ value: html, format: 'html' })} />
			</Suspense>
		</EditorBoundary>
	)
}

const Placeholder = styled.div`
	min-height: 8.438rem;
	border-radius: 5px;
	border: solid 1px #c4c4c4;
	background: #fafafa;
`

const FallbackArea = styled.textarea`
	border-radius: 5px;
	box-shadow: 0 5px 6px 0 rgba(0, 0, 0, 0.16);
	border: solid 1px #c4c4c4;
	width: 100%;
	resize: vertical;
	padding: 1rem 1.625rem;
	font-size: 0.875rem;
	min-height: 8.438rem;
`

export default RichTextAnswer
